import type {
  InventoryResource,
  RestoreAccount,
  Snapshot,
} from '@repo/api-gw-sdk';
import { Provider } from '@repo/api-gw-sdk';

import { useEnvironment } from '@/contexts/useEnvironment';
import { useBackupVaults } from '@/data/vaults/useBackupVaults';

import type { RestoreGenericStorageProps } from './bucketSelection';
import { FileConfiguration } from './steps/fileConfiguration';
import { SelectTargetAccountStep } from './steps/selectTargetAccount';

import type { Step } from '../wizard/Step';
import { Wizard } from '../wizard/Wizard';

export interface RestoreFileState extends RestoreGenericStorageProps {
  actionType: string;
  resourceId?: string;
  resource: InventoryResource;
  snapshot: Snapshot;
  supportedCloudProviders: Provider[];
  restoreAccount?: RestoreAccount;
  files: { path: string; isDirectory: boolean }[];
}

const restoreFileFlow: Step<RestoreFileState>[] = [
  {
    id: 'select_target_account_step',
    Component: SelectTargetAccountStep,
    next: [
      {
        id: 'file_configuration',
        Component: FileConfiguration,
      },
    ],
  },
];

interface Props {
  path: string;
  snapshot: Snapshot;
  resource: InventoryResource;
  isDirectory: boolean;
  onClose: () => void;
}

export const supportedCrossCloudRestores: Record<Provider, Provider[]> = {
  [Provider.Aws]: [Provider.Aws],
  [Provider.Azure]: [Provider.Azure, Provider.Aws, Provider.Gcp],
  [Provider.Gcp]: [Provider.Gcp],
  [Provider.MongoAtlas]: [], // No vaults in Atlas
  [Provider.ProviderUnspecified]: [],
};

export const RestoreFileWizard = (props: Props) => {
  const { vaults } = useBackupVaults();
  const { isDemo, isDev } = useEnvironment();

  const files = [{ path: props.path, isDirectory: props.isDirectory }];

  const cloudProvider = props.snapshot?.vaultId
    ? vaults.find((v) => v.id === props.snapshot.vaultId)?.vaultAttributes
        .cloudProvider
    : props.resource.cloudProvider;

  return (
    <Wizard<RestoreFileState>
      key={props.path}
      onFinish={async () => true}
      onAbort={props.onClose}
      flow={restoreFileFlow}
      initialState={{
        files,
        actionType: 'file',
        resource: props.resource,
        snapshot: props.snapshot,
        resourceId: props.resource.id,
        S3: {},
        GCS: {},
        StorageAccount: {},
        supportedCloudProviders:
          isDemo || isDev
            ? [Provider.Aws, Provider.Azure, Provider.Gcp]
            : ((cloudProvider && supportedCrossCloudRestores[cloudProvider]) ??
              []),
      }}
      title={'Restore File'}
      hideBackArrow
      stepperLabels={['Restore Account', 'Configuration']}
    />
  );
};
